import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  Platform,
  useWindowDimensions,
} from 'react-native';
import CourseMediaCarousel, { createCourseMediaCarouselStyles } from './CourseMediaCarousel';
import ModalVideoEmbed from './ModalVideoEmbed';
import { normalizeUserBreakVideoLinks } from '../utils/userBreakVideoLinks';

const CAROUSEL_ASPECT = 16 / 9;
const CAROUSEL_WIDTH_RATIO = 0.85;

function parseYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  const s = url.trim();
  const m =
    s.match(
      /(?:https?:\/\/)?(?:(?:www|m)\.)?youtube\.com\/(?:watch\?v=|embed\/|shorts\/)([^&?#/]+)/i
    ) || s.match(/(?:https?:\/\/)?youtu\.be\/([^&?#/]+)/i);
  return m ? m[1] : null;
}

function youtubeEmbedUrl(videoId) {
  if (!videoId) return '';
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?autoplay=1&playsinline=1&rel=0`;
}

function resolvePlayableUri(url) {
  const videoId = parseYouTubeVideoId(url);
  if (videoId) return youtubeEmbedUrl(videoId);
  return url;
}

/**
 * Carousel + inline player for one or more user_break video URLs.
 */
export default function UserBreakVideoPlayer({
  videoLinks,
  title,
  theme,
  maxContentWidth,
  autoPlaySingle = true,
}) {
  const { width: windowWidth } = useWindowDimensions();
  const urls = useMemo(() => normalizeUserBreakVideoLinks(videoLinks), [videoLinks]);
  const [player, setPlayer] = useState(null);

  const contentMaxWidth = maxContentWidth ?? Math.min(windowWidth - 40, 720);
  const slideWidth = Math.max(1, Math.round(contentMaxWidth * CAROUSEL_WIDTH_RATIO));
  const slideHeight = Math.round(slideWidth / CAROUSEL_ASPECT);
  const carouselStyles = useMemo(
    () => createCourseMediaCarouselStyles(theme, slideWidth, slideHeight),
    [theme, slideWidth, slideHeight]
  );

  useEffect(() => {
    if (!autoPlaySingle || urls.length !== 1) {
      if (urls.length !== 1) setPlayer(null);
      return;
    }
    setPlayer({ uri: resolvePlayableUri(urls[0]), title: title || 'Video' });
  }, [urls, title, autoPlaySingle]);

  const openVideo = useCallback(
    (uri, t) => {
      setPlayer({ uri, title: t || title || 'Video' });
    },
    [title]
  );

  if (!urls.length) return null;

  const playerBoxStyle = {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#000',
    borderRadius: 8,
    overflow: 'hidden',
  };

  if (urls.length === 1 && player) {
    return (
      <View style={playerBoxStyle}>
        <ModalVideoEmbed
          uri={player.uri}
          title={player.title}
          webViewStyle={{ flex: 1, width: '100%', minHeight: Platform.OS === 'web' ? undefined : 220 }}
        />
      </View>
    );
  }

  return (
    <View>
      <CourseMediaCarousel
        mediaUrls={urls}
        slideWidth={slideWidth}
        slideHeight={slideHeight}
        theme={theme}
        styles={carouselStyles}
        onOpenVideo={openVideo}
        pauseAutoRotate={!!player}
      />
      {player ? (
        <View style={[playerBoxStyle, { marginTop: 12 }]}>
          <ModalVideoEmbed
            uri={player.uri}
            title={player.title}
            webViewStyle={{ flex: 1, width: '100%', minHeight: Platform.OS === 'web' ? undefined : 220 }}
          />
        </View>
      ) : (
        <Text
          style={{
            marginTop: 8,
            textAlign: 'center',
            fontSize: 13,
            color: theme.colors.text,
            opacity: 0.75,
          }}
        >
          Tap a video to play
        </Text>
      )}
    </View>
  );
}
